import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Badge — inline status indicator.
 *
 * Semantic variants (`success`, `warning`, `danger`, `info`, `neutral`)
 * consume the token layer in `index.css`. Back-compat variants
 * (`default`, `secondary`, `destructive`, `outline`) are preserved for
 * existing shadcn consumers.
 *
 * Pairs with `Pill` (deprecated shim) — new code should use `<Badge>`.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium leading-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        // shadcn defaults
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-border text-foreground",
        // Semantic / status — subtle surface + accessible contrast text
        success:
          "border-transparent bg-success/10 text-success-foreground [color:hsl(var(--success))]",
        warning:
          "border-transparent bg-warning/15 text-warning-foreground [color:hsl(var(--warning))]",
        danger:
          "border-transparent bg-danger/10 text-danger-foreground [color:hsl(var(--danger))]",
        info: "border-transparent bg-info/10 text-info-foreground [color:hsl(var(--info))]",
        neutral:
          "border-border bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = ({ className, variant, ...props }: BadgeProps) => (
  <div className={cn(badgeVariants({ variant }), className)} {...props} />
);

export { Badge, badgeVariants };
